// components/Navbar.jsx
import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const roleLabel = {
    super_admin:  'Super Admin',
    system_admin: 'System Admin',
    tca:          'TCA',
    invigilator:  'Invigilator'
  }[user?.role] || user?.role || 'User';

  const roleColor = {
    super_admin:  ['#fff3e0', '#e65100'],
    system_admin: ['#e3f2fd', '#1565c0'],
    tca:          ['#f3e5f5', '#6a1b9a'],
    invigilator:  ['#e8f5e9', '#2e7d32']
  }[user?.role] || ['#f5f5f5', '#555'];

  const initials = user?.name
    ? user.name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)
    : 'U';

  // ✅ Nav links based on role
  const navLinks = (() => {
    const role = user?.role;
    if (role === 'super_admin' || role === 'system_admin') {
      return [
        { label: 'Dashboard',    path: '/dashboard',            icon: '🏠' },
        { label: 'Users',        path: '/users',                icon: '👥' },
        { label: 'Schedules',    path: '/schedules',            icon: '📅' },
        { label: 'Shifts',       path: '/shifts',               icon: '🕐' },
        { label: 'Remuneration', path: '/remuneration/summary', icon: '💰' },
        { label: 'Reports',      path: '/reports',              icon: '📊' },
      ];
    }
    if (role === 'tca') {
      return [
        { label: 'Dashboard',    path: '/test-center',          icon: '🏠' },
        { label: 'Schedules',    path: '/schedules',            icon: '📅' },
        { label: 'Shifts',       path: '/shifts',               icon: '🕐' },
        { label: 'Remuneration', path: '/remuneration/summary', icon: '💰' },
      ];
    }
    if (role === 'invigilator') {
      return [
        { label: 'Dashboard',    path: '/invigilator',          icon: '🏠' },
        { label: 'My Shifts',    path: '/my-shifts',            icon: '🕐' },
        { label: 'Remuneration', path: '/my-remuneration',      icon: '💰' },
      ];
    }
    return [];
  })();

  const handleLogout = () => {
    setDropdownOpen(false);
    logout();
    navigate('/login');
  };

  // ✅ Profile menu items — only navigate, no logout here
  const profileMenuItems = [
    { icon: '👤', label: 'My Profile',      path: '/profile' },
    { icon: '🔒', label: 'Change Password', path: '/change-password' },
  ];

  return (
    <nav style={{
      position: 'fixed',
      top: 0, left: 0, right: 0,
      height: '64px',
      backgroundColor: '#ffffff',
      borderBottom: '1px solid #f0f0f0',
      boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0 28px',
      zIndex: 1000,
      boxSizing: 'border-box'
    }}>

      {/* LEFT — Brand */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div style={{
          width: '36px', height: '36px',
          background: 'linear-gradient(135deg, #f1870e, #ff9800)',
          borderRadius: '10px',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '18px',
          boxShadow: '0 2px 8px rgba(241,135,14,0.4)'
        }}>
          🎓
        </div>
        <div>
          <div style={{ fontWeight: 800, fontSize: '16px', color: '#1a1a2e', lineHeight: 1.1 }}>
            ORC System
          </div>
          <div style={{ fontSize: '11px', color: '#f1870e', fontWeight: 600, letterSpacing: '0.5px' }}>
            Exam Management
          </div>
        </div>
      </div>

      {/* CENTER — Role-based Nav Links */}
      <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
        {navLinks.map(({ label, path, icon }) => {
          const isActive = location.pathname === path ||
            location.pathname.startsWith(path + '/');
          return (
            <button
              key={path}
              onClick={() => navigate(path)}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                padding: '7px 14px', borderRadius: '8px',
                border: 'none', cursor: 'pointer',
                fontSize: '13px', fontWeight: isActive ? 700 : 500,
                backgroundColor: isActive ? '#fff3e0' : 'transparent',
                color: isActive ? '#f1870e' : '#555',
                transition: 'all 0.15s',
                outline: 'none'
              }}
              onMouseEnter={e => {
                if (!isActive) {
                  e.currentTarget.style.backgroundColor = '#f9f9f9';
                  e.currentTarget.style.color = '#f1870e';
                }
              }}
              onMouseLeave={e => {
                if (!isActive) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = '#555';
                }
              }}
            >
              <span style={{ fontSize: '14px' }}>{icon}</span>
              {label}
            </button>
          );
        })}
      </div>

      {/* RIGHT — Profile Dropdown only */}
      <div ref={dropRef} style={{ position: 'relative' }}>
        <button
          onClick={() => setDropdownOpen(p => !p)}
          style={{
            display: 'flex', alignItems: 'center', gap: '10px',
            padding: '6px 10px 6px 6px', borderRadius: '12px',
            border: '1px solid #eee', backgroundColor: '#fafafa',
            cursor: 'pointer', transition: 'all 0.15s', outline: 'none'
          }}
          onMouseEnter={e => {
            e.currentTarget.style.borderColor = '#f1870e';
            e.currentTarget.style.backgroundColor = '#fff3e0';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.borderColor = '#eee';
            e.currentTarget.style.backgroundColor = '#fafafa';
          }}
        >
          {/* Avatar */}
          <div style={{
            width: '32px', height: '32px', borderRadius: '9px',
            background: 'linear-gradient(135deg, #f1870e, #ff9800)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'white', fontWeight: 700, fontSize: '13px',
            boxShadow: '0 2px 6px rgba(241,135,14,0.35)',
            flexShrink: 0
          }}>
            {initials}
          </div>

          <div style={{ textAlign: 'left' }}>
            <div style={{ fontSize: '13px', fontWeight: 700, color: '#1a1a2e', lineHeight: 1.2 }}>
              {user?.name || 'User'}
            </div>
            <div style={{
              display: 'inline-block',
              fontSize: '10px', fontWeight: 700,
              padding: '1px 6px', borderRadius: '4px',
              backgroundColor: roleColor[0], color: roleColor[1]
            }}>
              {roleLabel}
            </div>
          </div>

          <span style={{ fontSize: '10px', color: '#aaa', marginLeft: '2px' }}>
            {dropdownOpen ? '▲' : '▼'}
          </span>
        </button>

        {/* Dropdown */}
        {dropdownOpen && (
          <div style={{
            position: 'absolute', top: '52px', right: 0,
            width: '240px', backgroundColor: 'white',
            borderRadius: '14px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.14)',
            border: '1px solid #f0f0f0',
            overflow: 'hidden', zIndex: 9999
          }}>

            {/* Profile Header */}
            <div style={{
              padding: '20px',
              background: 'linear-gradient(135deg, #f1870e, #ff9800)',
              color: 'white'
            }}>
              <div style={{
                width: '48px', height: '48px', borderRadius: '14px',
                backgroundColor: 'rgba(255,255,255,0.25)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 800, fontSize: '18px', marginBottom: '10px',
                border: '2px solid rgba(255,255,255,0.4)'
              }}>
                {initials}
              </div>
              <div style={{ fontWeight: 700, fontSize: '15px' }}>
                {user?.name || 'User'}
              </div>
              <div style={{ fontSize: '12px', opacity: 0.85, marginTop: '2px' }}>
                {user?.email || ''}
              </div>
              <div style={{
                display: 'inline-block', marginTop: '8px',
                fontSize: '11px', fontWeight: 700,
                padding: '2px 8px', borderRadius: '6px',
                backgroundColor: 'rgba(255,255,255,0.25)',
                border: '1px solid rgba(255,255,255,0.4)'
              }}>
                {roleLabel}
              </div>
            </div>

            {/* Menu Items */}
            <div style={{ padding: '8px' }}>
              {profileMenuItems.map(({ icon, label, path }) => (
                <button
                  key={label}
                  onClick={() => { setDropdownOpen(false); navigate(path); }}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center',
                    gap: '10px', padding: '10px 12px', borderRadius: '8px',
                    border: 'none', backgroundColor: 'transparent',
                    cursor: 'pointer', fontSize: '13px', color: '#444',
                    fontWeight: 500, textAlign: 'left',
                    transition: 'all 0.15s', outline: 'none'
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.backgroundColor = '#fff3e0';
                    e.currentTarget.style.color = '#f1870e';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = '#444';
                  }}
                >
                  <span style={{ fontSize: '15px', width: '20px', textAlign: 'center' }}>
                    {icon}
                  </span>
                  {label}
                </button>
              ))}

              {/* Divider */}
              <div style={{ margin: '6px 0', borderTop: '1px solid #f0f0f0' }} />

              {/* Sign Out */}
              <button
                onClick={handleLogout}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center',
                  gap: '10px', padding: '10px 12px', borderRadius: '8px',
                  border: 'none', backgroundColor: 'transparent',
                  cursor: 'pointer', fontSize: '13px', color: '#c62828',
                  fontWeight: 600, textAlign: 'left',
                  transition: 'all 0.15s', outline: 'none'
                }}
                onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#ffebee'; }}
                onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                <span style={{ fontSize: '15px', width: '20px', textAlign: 'center' }}>🚪</span>
                Sign Out
              </button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;